import { zip } from 'fflate';
import { artifactPreviewUrl } from './artifact-preview-url';
import { fetchArtifactBytes } from './artifact-download';

export interface ZipEntry {
  readonly filename?: unknown;
  readonly downloadUrl?: unknown;
}

/** Refuse to buffer more than this in the tab. Every file has to be held in
 *  memory at once to be zipped, so a large export would otherwise risk an OOM
 *  crash — worse than the N-downloads it replaced. Callers fall back to
 *  per-file downloads past this point. */
export const ZIP_MAX_TOTAL_BYTES = 100 * 1024 * 1024;

/** Thrown when not one file could be fetched, so there is no archive to hand
 *  over. Distinct from a partial archive: some files failing is tolerated and
 *  reported, whereas all of them failing means the caller should try the other
 *  download mechanism instead. */
export class ZipEmptyError extends Error {
  readonly failed: number;
  constructor(failed: number) {
    super('No artifact could be fetched for the archive');
    this.name = 'ZipEmptyError';
    this.failed = failed;
    // See ZipTooLargeError: ES5 downlevel breaks `instanceof` on built-in
    // subclasses without this.
    Object.setPrototypeOf(this, ZipEmptyError.prototype);
  }
}

/** Thrown when a batch exceeds `ZIP_MAX_TOTAL_BYTES`.
 *
 *  A distinct type so the condition can't be mistaken for a per-file fetch
 *  failure: those are tolerated and counted, whereas this has to propagate to
 *  the caller, which falls back to streaming each file individually. */
export class ZipTooLargeError extends Error {
  constructor() {
    super('Artifact batch exceeds the in-memory zip limit');
    this.name = 'ZipTooLargeError';
    // Subclassing a built-in loses the prototype chain once TypeScript
    // downlevels to ES5, which silently breaks `instanceof` at runtime — not
    // just in tests. Restoring it explicitly is the standard workaround.
    Object.setPrototypeOf(this, ZipTooLargeError.prototype);
  }
}

// Re-compressing an already-compressed container wastes CPU for ~no gain, so
// these are stored rather than deflated. xlsx/docx are zip containers
// themselves; pdf and images carry their own compression.
const ALREADY_COMPRESSED = new Set([
  'zip',
  'xlsx',
  'xlsm',
  'xlsb',
  'docx',
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  '7z',
  'gz',
  'tgz',
]);

function extensionOf(filename: string): string {
  return filename.trim().toLowerCase().split('.').pop() ?? '';
}

/** De-duplicate names inside the archive: two artifacts in one message can
 *  share a filename, and a zip with repeated entries extracts unpredictably. */
function uniqueName(taken: Set<string>, filename: string): string {
  if (!taken.has(filename)) {
    taken.add(filename);
    return filename;
  }
  const dot = filename.lastIndexOf('.');
  const stem = dot === -1 ? filename : filename.slice(0, dot);
  const ext = dot === -1 ? '' : filename.slice(dot);
  let n = 2;
  let candidate = `${stem} (${n})${ext}`;
  while (taken.has(candidate)) {
    n += 1;
    candidate = `${stem} (${n})${ext}`;
  }
  taken.add(candidate);
  return candidate;
}

function zipAsync(files: Record<string, [Uint8Array, { level: 0 | 6 }]>) {
  return new Promise<Uint8Array>((resolve, reject) => {
    zip(files, (err, data) => {
      if (err) reject(err);
      else resolve(data);
    });
  });
}

export interface ZipResult {
  /** Files that could not be fetched. The archive contains the rest. */
  readonly failed: number;
}

/** Fetch every artifact and hand the browser a single archive.
 *
 *  Replaces triggering N downloads at once, which Chrome gates behind a
 *  "Download multiple files?" prompt — and silently drops files 2..N if the
 *  user declines. One same-origin blob download has no such gate.
 *
 *  A file that fails to fetch is skipped rather than failing the batch: a
 *  partial archive plus a count is more useful than nothing. If *nothing* can be
 *  fetched, or the batch is too large to buffer, this throws so the caller can
 *  fall back to per-file downloads — see ZipEmptyError / ZipTooLargeError. */
export async function downloadArtifactsAsZip(
  entries: readonly ZipEntry[],
  zipName = 'generated-files.zip'
): Promise<ZipResult> {
  const files: Record<string, [Uint8Array, { level: 0 | 6 }]> = {};
  const taken = new Set<string>();
  let failed = 0;
  let total = 0;

  for (const entry of entries) {
    const { downloadUrl, filename } = entry;
    if (typeof downloadUrl !== 'string' || !downloadUrl) {
      failed += 1;
      continue;
    }
    const name = typeof filename === 'string' && filename ? filename : 'file';
    // Only the fetch is tolerated per-file. The size check deliberately sits
    // outside this try: it must abort the whole archive so the caller's catch
    // runs and falls back to streaming per-file downloads. Throwing inside the
    // try would be swallowed as "one more failed file", and an oversized batch
    // would report an error instead of downloading via the path that still
    // works.
    let bytes: Uint8Array;
    try {
      bytes = await fetchArtifactBytes(artifactPreviewUrl(downloadUrl));
    } catch {
      failed += 1;
      continue;
    }

    total += bytes.byteLength;
    if (total > ZIP_MAX_TOTAL_BYTES) {
      throw new ZipTooLargeError();
    }
    const level = ALREADY_COMPRESSED.has(extensionOf(name)) ? 0 : 6;
    files[uniqueName(taken, name)] = [bytes, { level }];
  }

  // Nothing archived: throw rather than report, so the caller falls back to
  // per-file downloads. That fallback is not merely a retry of the same thing —
  // it fetches /presign and hands the URL to an iframe, where this path reads
  // bytes off /download. A failure mode that kills every read here (CORS on the
  // S3 redirect, say) can leave the presign path working perfectly, so
  // "everything failed" is precisely when trying the other mechanism pays off.
  if (Object.keys(files).length === 0) throw new ZipEmptyError(failed);

  const archive = await zipAsync(files);
  // Pass the Uint8Array itself, not `.buffer`: a view can be a window onto a
  // larger backing buffer, and handing over the whole buffer would append
  // whatever else lives in it to the archive — producing a zip some tools
  // refuse to extract.
  const blob = new Blob([archive], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = zipName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Same-origin blob: the browser has the bytes by the time click() returns,
    // so revoking immediately is safe and avoids leaking the whole archive for
    // the life of the page.
    URL.revokeObjectURL(url);
  }
  return { failed };
}
